import Layout from "../../components/Layout.tsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table.tsx";
import { Select } from "../../components/Select.tsx";
import { Text } from "../../components/Text.tsx";
import { useState, useEffect, useRef } from "react";
import { StepProps } from "./AddNewConnection.tsx";
import { useNewConnectionContext } from "../../context/NewConnectionContext.tsx";
import { useApplicationInfoContext } from "../../context/ApplicationInfoContext.tsx";
import { useExactVATCodes } from "../../hooks/data.ts";
import {
  fetchEasyflexBetalingskortingGrootboek,
  fetchEasyflexFinancialAdministration,
  fetchEasyflexGrootboeken,
} from "../../services/ApiService.ts";
import { SourceAdminstration, SyncModule } from "../../types.ts";

// Maps static Easyflex VAT code IDs to the corresponding field on EasyflexVATCodeEntry
const EASYFLEX_VAT_FIELD_MAP: Record<string, keyof import("../../types.ts").EasyflexVATCodeEntry> = {
  "17720": "nederlandHoog",
  "17721": "nederlandLaag",
  "17722": "nederland0",
  "17723": "verlegd",
  "17724": "binnenEu",
  "17725": "buitenEu",
};

const VAT_MODULE_COLUMNS: { module: SyncModule; label: string }[] = [
  { module: "INKOOP", label: "Exact Online - Inkoop" },
  { module: "VERKOOP", label: "Exact Online - Verkoop" },
];

interface VatFieldMapping {
  easyflexFieldId: string;
  easyflexFieldName: string;
  destinationByModule: Partial<Record<SyncModule, string>>;
}

const MapVATCodesStep = ({
  handleButtonBackClick,
  handleButtonNextClick,
  stepIndicator,
}: StepProps) => {
  const { newConnectionData, dispatch } = useNewConnectionContext();
  const { applicationInfo, dispatch: appInfoDispatch } = useApplicationInfoContext();
  const { exactVATCodes, loading, error } = useExactVATCodes(
    newConnectionData.destinationAdministrationId,
    newConnectionData.systemAuthorizationId,
    applicationInfo.availableDestinationVATCodes
  );

  const easyflexVATs = applicationInfo.availableEasyVATCodes;

  const journalMappings = newConnectionData.mappings?.filter((mapping) => mapping.type === "journal") ?? [];
  const selectedModules = VAT_MODULE_COLUMNS.filter(({ module }) =>
    journalMappings.some((mapping) => mapping.source.toUpperCase() === module)
  );

  const vatCodesForModule = (module: SyncModule, selectedCode?: string) => {
    const transactionTypes: Record<SyncModule, string> = { VERKOOP: "S", INKOOP: "P", LOON: "" };
    return exactVATCodes.filter(
      (vatCode) =>
        vatCode.VATTransactionType === "B" ||
        vatCode.VATTransactionType === transactionTypes[module] ||
        vatCode.Code === selectedCode
    );
  };

  // Save fetched VAT codes to context immediately so they persist when navigating back
  useEffect(() => {
    if (exactVATCodes.length > 0 && applicationInfo.availableDestinationVATCodes.length === 0) {
      appInfoDispatch({ type: "UPDATE_DESTINATION_VAT_CODES", codes: exactVATCodes });
    }
  }, [exactVATCodes, applicationInfo.availableDestinationVATCodes.length, appInfoDispatch]);

  // Fetch Easyflex financial administration data and cache in context for pre-filling
  useEffect(() => {
    if (!newConnectionData.sourceAdministrationId || applicationInfo.easyflexFinancialAdmin !== null) return;

    void fetchEasyflexFinancialAdministration(
      newConnectionData.sourceAdministrationId,
      new Date().getFullYear(),
    )
      .then((data) => appInfoDispatch({ type: "UPDATE_EASYFLEX_FINANCIAL_ADMIN", data }))
      .catch(() => {
        // silently ignore — pre-fill just won't happen
      });
  }, [newConnectionData.sourceAdministrationId, applicationInfo.easyflexFinancialAdmin, appInfoDispatch]);

  // Check if we have existing VAT code mappings in context
  const existingVATMappings = newConnectionData.mappings?.filter(mapping => mapping.type === "vatCode") || [];

  const emptyMappings: VatFieldMapping[] = easyflexVATs.map((easyflexVAT) => {
    const easyflexFieldId = String(easyflexVAT.id);
    const destinationByModule: Partial<Record<SyncModule, string>> = {};
    for (const { module } of selectedModules) {
      const existingMapping = existingVATMappings.find(
        (mapping) => mapping.source === easyflexFieldId && mapping.module === module
      );
      if (existingMapping) destinationByModule[module] = existingMapping.destination;
    }
    return {
      easyflexFieldId,
      easyflexFieldName: easyflexVAT.name,
      destinationByModule,
    };
  });

  const [fieldMappings, setFieldMappings] = useState(emptyMappings);

  // Pre-fill from Easyflex financial admin when both VAT code lists are ready
  const hasPreFilled = useRef(false);
  useEffect(() => {
    if (hasPreFilled.current) return;
    const financialAdmin = applicationInfo.easyflexFinancialAdmin;
    if (!financialAdmin || exactVATCodes.length === 0) return;
    if (existingVATMappings.length > 0) return;

    const vatEntryByModule: Partial<Record<SyncModule, typeof financialAdmin.codesVerkopen[number]>> = {
      VERKOOP: financialAdmin.codesVerkopen[0],
      INKOOP: financialAdmin.codesInkopen[0],
    };

    setFieldMappings((prev) =>
      prev.map((mapping) => {
        const field = EASYFLEX_VAT_FIELD_MAP[mapping.easyflexFieldId];
        const destinationByModule = { ...mapping.destinationByModule };
        for (const { module } of selectedModules) {
          if (destinationByModule[module]) continue;
          const vatEntry = vatEntryByModule[module];
          const code = field && vatEntry ? vatEntry[field] : null;
          if (code && vatCodesForModule(module).some((vatCode) => vatCode.Code === code)) {
            destinationByModule[module] = code;
          }
        }
        return { ...mapping, destinationByModule };
      })
    );
    hasPreFilled.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [applicationInfo.easyflexFinancialAdmin, exactVATCodes]);

  const isInkoopSelected = selectedModules.some(({ module }) => module === "INKOOP");
  const existingBetalingskortingMapping = newConnectionData.mappings?.find(
    (mapping) => mapping.type === "betalingskorting"
  );
  const [betalingskortingGrootboek, setBetalingskortingGrootboek] = useState(
    existingBetalingskortingMapping?.destination ?? ""
  );
  const [grootboeken, setGrootboeken] = useState<SourceAdminstration[]>([]);
  const [grootboekenLoading, setGrootboekenLoading] = useState(false);

  // Fetch Easyflex grootboeken, and pre-fill the betalingskorting grootboek from the latest inkoop export
  useEffect(() => {
    if (!isInkoopSelected || !newConnectionData.sourceAdministrationId) return;

    setGrootboekenLoading(true);
    void fetchEasyflexGrootboeken(newConnectionData.sourceAdministrationId)
      .then(setGrootboeken)
      .catch(() => setGrootboeken([]))
      .finally(() => setGrootboekenLoading(false));

    if (existingBetalingskortingMapping) return;
    void fetchEasyflexBetalingskortingGrootboek(newConnectionData.sourceAdministrationId)
      .then(({ grootboek }) => {
        if (grootboek) setBetalingskortingGrootboek((current) => current || grootboek);
      })
      .catch(() => {
        // silently ignore — pre-fill just won't happen
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listing the existing mapping would refetch the grootboeken whenever it changes
  }, [isInkoopSelected, newConnectionData.sourceAdministrationId]);

  const handleFieldMappingChange = (easyflexFieldId: string, module: SyncModule, destinationFieldId: string) => {
    setFieldMappings((prev) =>
      prev.map((mapping) =>
        mapping.easyflexFieldId === easyflexFieldId
          ? { ...mapping, destinationByModule: { ...mapping.destinationByModule, [module]: destinationFieldId } }
          : mapping
      )
    );
  };

  const onButtonNextClick = () => {
    const missingDestination = fieldMappings.some((mapping) =>
      selectedModules.some(({ module }) => !mapping.destinationByModule[module])
    );
    if (missingDestination) {
      alert("Kies voor elk Easyflex-veld en elke module een Exact Online-veld!");
      return;
    }
    const fieldMappingIds = fieldMappings.flatMap((mapping) =>
      selectedModules.map(({ module }) => ({
        source: mapping.easyflexFieldId,
        destination: mapping.destinationByModule[module]!,
        type: "vatCode",
        module,
      }))
    );

    const betalingskortingMappings =
      isInkoopSelected && betalingskortingGrootboek
        ? [
            {
              source: "betalingskorting",
              destination: betalingskortingGrootboek,
              type: "betalingskorting",
              module: "INKOOP" as SyncModule,
            },
          ]
        : [];

    // Preserve existing journal mappings and replace the VAT code and betalingskorting mappings
    const existingJournalMappings = newConnectionData.mappings?.filter(mapping => mapping.type === "journal") || [];
    const allMappings = [...fieldMappingIds, ...betalingskortingMappings, ...existingJournalMappings];

    dispatch({ type: "UPDATE_FIELD", key:'mappings',value: allMappings });

    handleButtonNextClick?.();
  }

  const isUpdating = newConnectionData.id && newConnectionData.id > 0;

  return (
    <Layout
      title={isUpdating ? "Koppeling aanpassen" : "Nieuwe koppeling maken"}
      stepTitle="BTW-codes koppelen"
      onButtonBackClick={handleButtonBackClick}
      onButtonNextClick={onButtonNextClick}
      stepIndicator={stepIndicator}
    >
      <Text className="mb-12">
        Koppel voor elk Easyflex-veld een Exact Online-veld, per module.
      </Text>
      <Table dense>
        <TableHead>
          <TableRow>
            <TableHeader>Easyflex Veld</TableHeader>
            {selectedModules.map(({ module, label }) => (
              <TableHeader key={module}>{label}</TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {fieldMappings.map((mapping) => (
            <TableRow key={mapping.easyflexFieldId}>
              <TableCell>{mapping.easyflexFieldName}</TableCell>
              {selectedModules.map(({ module }) => (
                <TableCell key={module}>
                  <Select
                    name="exactField"
                    onChange={(e) => handleFieldMappingChange(mapping.easyflexFieldId, module, e.target.value)}
                    value={mapping.destinationByModule[module] ?? ""}
                    disabled={loading}
                  >
                    {loading ? (
                      <option value="" disabled>Laden...</option>
                    ) : error ? (
                      <option value="" disabled>{error}</option>
                    ) : (
                      <>
                        <option value="" disabled>Selecteer één veld</option>
                        {vatCodesForModule(module, mapping.destinationByModule[module]).map((destinationVATCode) => (
                          <option key={destinationVATCode.Code} value={destinationVATCode.Code}>
                            {destinationVATCode.Description}
                          </option>
                        ))}
                      </>
                    )}
                  </Select>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isInkoopSelected && (
        <div className="mt-12">
          <Text className="mb-4 font-medium">Betalingskorting (inkoop)</Text>
          <Text className="mb-4">
            Optioneel: het grootboek waarop de betalingskorting wordt geboekt.
          </Text>
          <Select
            name="betalingskortingGrootboek"
            onChange={(e) => setBetalingskortingGrootboek(e.target.value)}
            value={betalingskortingGrootboek}
            disabled={grootboekenLoading}
          >
            {grootboekenLoading ? (
              <option value="">Laden...</option>
            ) : (
              <>
                <option value="">Niet ingesteld</option>
                {grootboeken.map((grootboek) => (
                  <option key={grootboek.id} value={String(grootboek.id)}>
                    {grootboek.id} - {grootboek.name}
                  </option>
                ))}
              </>
            )}
          </Select>
        </div>
      )}
    </Layout>
  );
};

export default MapVATCodesStep;
